/**
 * Derive the 4-tile hero timeline composition from a series's real past sessions,
 * real upcoming sessions, and (optionally) synthetic past dates from the scarcity
 * config.
 *
 * Composition rules (origin R1, R4, R5; plan U2):
 *   - Past slots (left): prefer the 2 most recent REAL past sessions (sorted desc).
 *     If fewer than 2 real past exist, fill the missing EARLIER slots from the
 *     synthetic-past config. A synthetic date is suppressed if it would land at or
 *     after any real past date for that series (collision rule).
 *   - Upcoming slots (right): the next 2 upcoming bookable sessions (sorted asc).
 *     When only 1 upcoming exists, the 4th slot is a "more coming soon" placeholder.
 *     When 0 upcoming exist, the entire timeline is suppressed (return null).
 *
 * Fixed-width mode (`totalSlots`, opt-in; Sales passes 3): the strip is always
 * `totalSlots` cards wide — past tiles absorb every slot the upcoming sessions
 * do not fill, there is no placeholder, and zero upcoming yields a past-only
 * composition instead of null. Omitting `totalSlots` keeps the 2 + 2 default.
 *
 * Plan: docs/plans/2026-05-26-001-feat-workshop-scarcity-display-plan.md (U2)
 */

import type { SeriesScarcityConfig } from "@/lib/scarcity-config";

export type RealPastInput = {
  id: number;
  startDatetime: string | Date;
  timezone: string;
};

export type UpcomingInput = {
  id: number;
  startDatetime: string | Date;
  endDatetime: string | Date;
  timezone: string;
  status: string;
  seatsSold?: number | null;
  maxCapacity?: number | null;
};

export type PastTile =
  | {
      kind: "real";
      key: string;
      startDatetime: Date;
      timezone: string;
    }
  | {
      kind: "synthetic";
      key: string;
      startDatetime: Date;
      ariaLabel: string;
    };

export type UpcomingTile = {
  kind: "upcoming";
  session: UpcomingInput;
  startDatetime: Date;
};

export type PlaceholderTile = {
  kind: "placeholder";
  label: "More dates coming soon";
};

export type TimelineComposition = {
  pastTiles: PastTile[];
  upcomingTiles: UpcomingTile[];
  placeholder: PlaceholderTile | null;
};

const TIMELINE_PAST_SLOTS = 2;
const TIMELINE_UPCOMING_SLOTS = 2;

/** Parse a date input, returning null when malformed. */
function safeDate(input: string | Date): Date | null {
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Returns the composed timeline, or `null` when the series has zero upcoming
 * sessions (suppresses the whole timeline; caller falls back to legacy behavior).
 * In fixed-width mode (`totalSlots`) zero upcoming returns past-only tiles instead.
 */
export function deriveTimelineTiles({
  realPast,
  upcoming,
  config,
  totalSlots,
}: {
  realPast: RealPastInput[];
  upcoming: UpcomingInput[];
  config: SeriesScarcityConfig | null;
  /** Fixed-width mode (Sales passes 3): up to 2 upcoming tiles, and past tiles
   *  fill every remaining slot, oldest to newest. No "more dates coming soon"
   *  placeholder, and zero upcoming returns past-only tiles rather than null,
   *  so the strip always shows `totalSlots` cards when enough dates exist.
   *  Omit for the original 2 + 2 + placeholder composition. */
  totalSlots?: number;
}): TimelineComposition | null {
  // Parse and filter. Sessions with malformed startDatetime are dropped silently.
  const realPastParsed = realPast
    .map((s) => {
      const dt = safeDate(s.startDatetime);
      return dt ? { ...s, startDatetime: dt } : null;
    })
    .filter((s): s is { id: number; startDatetime: Date; timezone: string } => s !== null);

  const upcomingParsed = upcoming
    .map((s) => {
      const dt = safeDate(s.startDatetime);
      return dt ? { ...s, startDatetime: dt } : null;
    })
    .filter((s): s is UpcomingInput & { startDatetime: Date } => s !== null);

  const fixed = typeof totalSlots === "number" && totalSlots > 0;
  // Zero upcoming suppresses the timeline entirely in the original mode.
  if (upcomingParsed.length === 0 && !fixed) return null;

  // Upcoming: take next N sorted ascending.
  const upcomingSorted = [...upcomingParsed].sort(
    (a, b) => a.startDatetime.getTime() - b.startDatetime.getTime(),
  );
  const upcomingSlots = fixed ? Math.min(TIMELINE_UPCOMING_SLOTS, totalSlots) : TIMELINE_UPCOMING_SLOTS;
  const upcomingTiles: UpcomingTile[] = upcomingSorted
    .slice(0, upcomingSlots)
    .map((s) => ({ kind: "upcoming" as const, session: s, startDatetime: s.startDatetime }));
  const pastSlots = fixed ? Math.max(0, totalSlots - upcomingTiles.length) : TIMELINE_PAST_SLOTS;

  // Past: take most recent N real past, sorted descending by date.
  const realPastSorted = [...realPastParsed].sort(
    (a, b) => b.startDatetime.getTime() - a.startDatetime.getTime(),
  );
  const realPastTiles: PastTile[] = realPastSorted.slice(0, pastSlots).map((s) => ({
    kind: "real" as const,
    key: `real-${s.id}`,
    startDatetime: s.startDatetime,
    timezone: s.timezone,
  }));

  // If the real past already fills every past slot, ignore synthetic entirely.
  let pastTiles: PastTile[] = realPastTiles;
  if (realPastTiles.length < pastSlots && config?.syntheticPast?.length) {
    // Synthetic dates are only used to fill EARLIER slots than any real past.
    // The earliest real past date is the cutoff — synthetic dates must be earlier.
    const earliestRealMs = realPastTiles.length
      ? realPastTiles[realPastTiles.length - 1].startDatetime.getTime()
      : Number.POSITIVE_INFINITY;

    const syntheticTiles: PastTile[] = config.syntheticPast
      .map((entry) => {
        const dt = safeDate(entry.date);
        if (!dt || dt.getTime() >= earliestRealMs) return null;
        return {
          kind: "synthetic" as const,
          key: `synthetic-${entry.date}`,
          startDatetime: dt,
          ariaLabel: entry.ariaLabel,
        };
      })
      .filter((t): t is Extract<PastTile, { kind: "synthetic" }> => t !== null)
      .sort((a, b) => b.startDatetime.getTime() - a.startDatetime.getTime());

    // Fill gaps: real tiles (newest-first) + synthetic tiles (newest-first), capped at N.
    pastTiles = [...realPastTiles, ...syntheticTiles].slice(0, pastSlots);
  }

  // Past tiles render left-to-right oldest-to-newest in the chronological strip,
  // so reverse to get ascending order for rendering.
  pastTiles = pastTiles.reverse();

  const placeholder: PlaceholderTile | null =
    !fixed && upcomingTiles.length < TIMELINE_UPCOMING_SLOTS
      ? { kind: "placeholder", label: "More dates coming soon" }
      : null;

  return { pastTiles, upcomingTiles, placeholder };
}
